import React from "react";
import useSWR, { mutate } from "swr";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DownloadSimple, Sparkle, PaperPlaneRight } from "@phosphor-icons/react";
import { fetcher, downloadXlsx } from "./common";
import DocumentPreview, { downloadDocx } from "@/components/app/DocumentPreview";
import { qcStatusLabel } from "@/lib/labels";

/** Travaux de fin d'exercice (classeur DSF) + compte rendu client généré par IA, soumis à validation expert. */
export default function DsfPanel({ file }) {
  const [year, setYear] = React.useState(String(new Date().getFullYear()));
  const key = `/accounting/files/${file.id}/dsf-report?year=${year}`;
  const { data: report, error } = useSWR(key, fetcher, {
    refreshInterval: (r) => (r?.status === "en_generation" ? 2500 : 0),
    shouldRetryOnError: false,
  });
  const { data: missions = [] } = useSWR("/missions", fetcher);
  const clientMissions = missions.filter((m) => m.client_id === file.client_id);
  const [qcOpen, setQcOpen] = React.useState(false);
  const [missionId, setMissionId] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  if (file.chart_type !== "syscohada") {
    return (
      <div className="border-t border-zinc-200 pt-6" data-testid="dsf-panel">
        <div className="border border-dashed border-zinc-300 rounded-md p-8 text-center text-sm text-zinc-500">
          Les travaux de fin d'exercice et le compte rendu DSF sont disponibles pour les dossiers en plan SYSCOHADA.
        </div>
      </div>
    );
  }

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/accounting/files/${file.id}/dsf-report`, { year });
      toast.success("Génération du compte rendu lancée");
      mutate(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Génération impossible"); }
    finally { setBusy(false); }
  };

  const submitQc = async () => {
    setBusy(true);
    try {
      await api.post(`/accounting/files/${file.id}/dsf-report/${report.id}/submit-qc`, { mission_id: missionId });
      toast.success("Compte rendu soumis pour validation par l'expert-comptable");
      setQcOpen(false);
      mutate(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Soumission impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 border-t border-zinc-200 pt-6" data-testid="dsf-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Travaux de fin d'exercice & compte rendu DSF</div>
          <div className="w-32"><Label>Exercice</Label><Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="dsf-year" /></div>
        </div>
        <Button variant="outline" onClick={() => downloadXlsx(`/accounting/files/${file.id}/dsf-travaux?year=${year}`, `travaux_fin_exercice_${year}_${file.name}.xlsx`)} data-testid="dsf-travaux-export">
          <DownloadSimple size={16} className="mr-1" /> Classeur travaux de fin d'exercice
        </Button>
      </div>

      <div className="border border-zinc-200 bg-white rounded-md p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <div className="font-medium">Compte rendu DSF — Exercice {year}</div>
            <div className="text-xs text-zinc-500">Note de revue de clôture rédigée par l'IA, à valider par l'expert-comptable.</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {report?.status === "pret" && (
              <>
                <Button variant="outline" size="sm" onClick={() => downloadDocx(report.title, report.content)} data-testid="dsf-report-download">
                  <DownloadSimple size={14} className="mr-1" /> Word
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadXlsx(`/accounting/files/${file.id}/dsf-report/${report.id}/pdf`, `${report.title.replace(/[^\w\-]+/g, "_")}.pdf`)} data-testid="dsf-report-pdf">
                  <DownloadSimple size={14} className="mr-1" /> PDF
                </Button>
                {report.qc_review_id ? (
                  <Link to={`/controle-qualite/${report.qc_review_id}`} data-testid="dsf-qc-link"
                    className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100">
                    Contrôle qualité : {qcStatusLabel(report.qc_status || "a_relire")}
                  </Link>
                ) : (
                  <Button size="sm" onClick={() => setQcOpen(true)} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="dsf-report-submit-qc">
                    <PaperPlaneRight size={14} className="mr-1" /> Soumettre pour validation
                  </Button>
                )}
              </>
            )}
            {(!report || report.status === "erreur") && (
              <Button size="sm" onClick={generate} disabled={busy} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="dsf-report-generate">
                <Sparkle size={14} className="mr-1" /> {report?.status === "erreur" ? "Relancer la génération" : "Générer le compte rendu"}
              </Button>
            )}
            {report?.status === "pret" && (
              <Button variant="outline" size="sm" onClick={generate} disabled={busy} data-testid="dsf-report-regenerate">Régénérer</Button>
            )}
          </div>
        </div>

        {error && <p className="text-sm text-zinc-500" data-testid="dsf-report-empty">Aucun compte rendu généré pour cet exercice.</p>}
        {report?.status === "en_generation" && (
          <div className="p-8 text-center text-sm text-zinc-500" data-testid="dsf-report-generating">
            <span className="inline-block w-4 h-4 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mr-2 align-middle"></span>
            Rédaction du compte rendu en cours…
          </div>
        )}
        {report?.status === "erreur" && (
          <div className="border border-red-200 bg-red-50 rounded-md p-4 text-sm text-red-800" data-testid="dsf-report-error">{report.error}</div>
        )}
        {report?.status === "pret" && report.content && (
          <div data-testid="dsf-report-preview"><DocumentPreview content={report.content} /></div>
        )}
      </div>

      <Dialog open={qcOpen} onOpenChange={setQcOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Soumettre pour validation</DialogTitle>
            <DialogDescription>Le compte rendu sera soumis au module Contrôle qualité ; seul un expert ou administrateur pourra le valider.</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="dsf-qc-mission">Mission du client</Label>
            <Select value={missionId} onValueChange={setMissionId}>
              <SelectTrigger id="dsf-qc-mission" data-testid="dsf-qc-mission"><SelectValue placeholder="Sélectionner une mission" /></SelectTrigger>
              <SelectContent>
                {clientMissions.map((m) => (<SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>))}
              </SelectContent>
            </Select>
            {clientMissions.length === 0 && <p className="text-xs text-zinc-500 mt-1">Aucune mission pour ce client — créez-en une dans la page Missions.</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setQcOpen(false)}>Annuler</Button>
            <Button onClick={submitQc} disabled={!missionId || busy} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="dsf-qc-confirm">
              {busy ? "Envoi…" : "Soumettre"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
